import axios from "axios";
import appConfig from "configs/app.config";
import { TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY } from "constants/api.constant";
import { PERSIST_STORE_NAME } from "constants/app.constant";
import deepParseJson from "utils/deepParseJson";
import store from "../store";
import { onSignOutSuccess } from "../store/auth/sessionSlice";
import { Notification, toast } from "components/ui";
import queryString from "query-string";
import { getItem } from "utils/safeStorage";
import { getApiErrorMessage } from "utils/getApiErrorMessage";

const unauthorizedCode = [401];

const FALLBACK_ERROR_TEXT = "İşlem başarısız. Lütfen tekrar deneyin.";

/**
 * Notification children'a doğrudan `errResponse.data` verilmesi, API nesne
 * döndürdüğünde "Objects are not valid as a React child" hatasına düşüyordu.
 */
const toDisplayText = (error) => {
  const message = getApiErrorMessage(error);
  if (message) return message;

  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();

  return FALLBACK_ERROR_TEXT;
};

/** Giriş yapılmadığında /error ve /access-denied korumalı rotalar olduğu için
 * hard redirect kullanıcıyı /welcome'a savuruyor; bu durumda toast yeterli. */
const isSignedIn = () => {
  const persistData = deepParseJson(getItem(PERSIST_STORE_NAME));
  return Boolean(persistData?.auth?.session?.token);
};

const BaseService = axios.create({
  timeout: 60000,
  baseURL: appConfig.apiPrefix,
});

BaseService.interceptors.request.use(
  (config) => {
    const rawPersistData = getItem(PERSIST_STORE_NAME);
    const persistData = deepParseJson(rawPersistData);

    const accessToken = persistData?.auth?.session?.token;

    if (accessToken) {
      config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

BaseService.interceptors.response.use(
  (response) => response,
  (error) => {
    const errResponse = error?.response;
    if (!errResponse) {
      return Promise.reject(error);
    }
    if (error?.config?.skipErrorHandler) {
      return Promise.reject(error);
    }
    const signedIn = isSignedIn();

    if (errResponse?.status === 422 && signedIn) {
      let payload = {
        // responseUrl: errResponse.request.responseURL,
        // data: errResponse.config.data,
        serverMessage: toDisplayText(error),
      };
      window.location.href = `/access-denied?${queryString.stringify(payload)}`;
    } else if (errResponse?.status === 500 && signedIn) {
      let payload = {
        responseUrl: errResponse.request?.responseURL,
        data: errResponse.config?.data,
        serverMessage: errResponse?.data?.Detail,
      };
      window.location.href = `/error?${queryString.stringify(payload)}`;
    } else if (errResponse?.data?.errors) {
      const errorLines = Object.values(errResponse.data.errors ?? {})
        .map((item) => (Array.isArray(item) ? item[0] : item))
        .filter((item) => typeof item === "string" && item.trim());

      toast.push(
        <Notification
          title={
            typeof errResponse?.data?.title === "string"
              ? errResponse.data.title
              : "İşlem Başarısız!"
          }
          type="danger"
          duration={3500}
          width={500}
        >
          <div className="grid gap-2.5">
            {errorLines.length ? (
              errorLines.map((item, i) => <span key={i}>{item}</span>)
            ) : (
              <span>{FALLBACK_ERROR_TEXT}</span>
            )}
          </div>
        </Notification>,
        {
          placement: "top-center",
        }
      );
    } else {
      toast.push(
        <Notification title={"İşlem Başarısız!"} type="danger" duration={3500}>
          {toDisplayText(error)}
        </Notification>,
        {
          placement: "top-center",
        }
      );
    }
    if (errResponse && unauthorizedCode.includes(errResponse.status)) {
      store.dispatch(onSignOutSuccess());
      // Giriş ekranındaki 401'lerde reload etmek formu sıfırlıyor (ve döngü riski var).
      if (window.location.pathname !== "/sign-in") {
        window.location.href = `/sign-in`;
      }
    }
    return Promise.reject(error);
  }
);

export default BaseService;
